import { Avatar, IconButton } from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { SocketContext } from "@/context/socket";
import { IEdoChatMessage } from "@/shared/model/edo-chat-message.model";
import { IEdoChat } from "@/shared/model/edo-chat.model";
import { IEdoChatParticipation } from "@/shared/model/edo-chat-participation.model";
import { EdoChatType } from "@/shared/model/enumerations/edo-chat-type.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import {
  ChatState,
  closeChatWindow,
  minimizeChatWindow,
} from "@/shared/reducers/chat.reducer";
import {
  checkAllMessagesByChat,
  getUnseenMessagesCount,
} from "@/shared/reducers/entities/edo-chat-message.reducer";
import { cn } from "@/shared/util/cn";
import axios from "axios";
import { Maximize2, Minus, Send, User, Users, X } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export interface FloatingChatWindowProps {
  chatId: number;
  rightOffset: number;
}

const FloatingChatWindow: React.FC<FloatingChatWindowProps> = ({
  chatId,
  rightOffset,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const socket = useContext(SocketContext);
  const messageAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [participation, setParticipation] = useState<IEdoChatParticipation | null>(null);
  const [messages, setMessages] = useState<IEdoChatMessage[]>([]);
  const [messageToSend, setMessageToSend] = useState("");
  const [loading, setLoading] = useState(true);

  const { account } = useAppSelector<AuthenticationState>((s) => s.authentication);
  const { participations } = useAppSelector<ChatState>((s) => s.chat);

  useEffect(() => {
    const found = participations.find((p) => p.chat?.id === chatId);
    if (found) {
      setParticipation(found);
    } else {
      // Fallback: fetch participation from API
      axios
        .get<{ data: IEdoChatParticipation }>(`/chats/${chatId}/participation`)
        .then((res) => setParticipation(res.data.data))
        .catch(() => {});
    }
  }, [chatId, participations]);

  useEffect(() => {
    let cancelled = false;
    const loadMessages = async () => {
      setLoading(true);
      try {
        const response = await axios.get("/chat-messages", {
          params: {
            page: 0,
            limit: 30,
            sortBy: "createdAt:ASC",
            "filter.chat.id": "$eq:" + chatId,
          },
        });
        if (!cancelled) setMessages([...response.data.data]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [chatId]);

  // Mark as read on mount and whenever a new message arrives in this chat
  useEffect(() => {
    if (!chatId) return;
    dispatch(checkAllMessagesByChat(String(chatId)));
    dispatch(getUnseenMessagesCount());
  }, [chatId, dispatch, messages.length]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Listen to incoming socket messages for this chat (de-duplicated against optimistic ones)
  useEffect(() => {
    const handler = (msg: IEdoChatMessage) => {
      if (msg.chat?.id !== chatId) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        // Replace any optimistic placeholder authored by the same user with same body
        const placeholderIdx = prev.findIndex(
          (m) =>
            (m as IEdoChatMessage & { __optimistic?: boolean }).__optimistic &&
            m.user?.id === msg.user?.id &&
            m.message === msg.message,
        );
        if (placeholderIdx >= 0) {
          const next = [...prev];
          next[placeholderIdx] = msg;
          return next;
        }
        return [...prev, msg];
      });
    };
    socket.on("message", handler);
    return () => {
      socket.off("message", handler);
    };
  }, [socket, chatId]);

  const sendMessage = () => {
    const trimmed = messageToSend.trim();
    if (!trimmed) return;
    const chat: IEdoChat = { id: chatId };
    socket.emit("message", { message: trimmed, chat });
    if (account) {
      const optimistic = {
        id: -Date.now(),
        message: trimmed,
        chat,
        user: account,
        createdAt: new Date().toISOString(),
        __optimistic: true,
      } as unknown as IEdoChatMessage;
      setMessages((prev) => [...prev, optimistic]);
    }
    setMessageToSend("");
    inputRef.current?.focus();
  };

  const handleClose = () => {
    dispatch(closeChatWindow(chatId));
  };

  const handleMinimize = () => {
    dispatch(minimizeChatWindow(chatId));
  };

  const handleExpand = () => {
    navigate(`/chat/${chatId}`);
    dispatch(closeChatWindow(chatId));
  };

  const isGroup = participation?.chat?.type === EdoChatType.GROUP;
  const title = participation
    ? isGroup
      ? participation.chat.title
      : participation.title
    : "Chat";
  const initials = title
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

  return (
    <div
      className="pointer-events-auto fixed bottom-0 z-40 flex h-[460px] w-[320px] flex-col overflow-hidden rounded-t-3xl border border-b-0 border-[var(--color-mockup-border)] bg-surface shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"
      style={{ right: `${rightOffset}px` }}
      role="dialog"
      aria-label={title ? `Chat con ${title}` : "Chat"}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/40 px-3 py-2">
        <button
          type="button"
          onClick={handleExpand}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left transition-opacity hover:opacity-80"
          aria-label="Apri chat completa"
        >
          <Avatar className="h-8 w-8 shrink-0 text-xs">
            {participation ? (
              isGroup ? (
                <Users className="h-4 w-4" />
              ) : (
                initials
              )
            ) : (
              <User className="h-4 w-4" />
            )}
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate font-heading text-sm font-bold text-[var(--color-mockup-text)]">
              {title}
            </p>
            <p className="truncate text-[10px] text-[var(--color-mockup-text-light)]">
              {isGroup ? "Gruppo" : "Online"}
            </p>
          </div>
        </button>
        <IconButton
          size="sm"
          onClick={handleExpand}
          aria-label="Apri pagina chat"
          className="h-7 w-7"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          onClick={handleMinimize}
          aria-label="Minimizza chat"
          className="h-7 w-7"
        >
          <Minus className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton
          size="sm"
          onClick={handleClose}
          aria-label="Chiudi chat"
          className="h-7 w-7"
        >
          <X className="h-3.5 w-3.5" />
        </IconButton>
      </div>

      {/* Messages */}
      <div
        ref={messageAreaRef}
        className="flex-1 overflow-y-auto bg-[var(--color-page)] px-3 py-3"
      >
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : messages.length === 0 ? (
          <p className="mt-8 text-center text-xs text-[var(--color-mockup-text-light)]">
            Nessun messaggio. Inizia la conversazione!
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {messages.map((m) => {
              const mine = m.user?.id === account?.id;
              return (
                <li
                  key={m.id}
                  className={cn(
                    "flex w-full",
                    mine ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[78%] rounded-2xl px-3 py-2 text-sm leading-snug shadow-sm",
                      mine
                        ? "rounded-br-md bg-primary text-white"
                        : "rounded-bl-md bg-surface text-[var(--color-mockup-text)] border border-[var(--color-mockup-border)]"
                    )}
                  >
                    {!mine && isGroup && (
                      <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide opacity-70">
                        {m.user?.firstName} {m.user?.lastName}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap break-words">{m.message}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Input */}
      <div className="flex shrink-0 items-center gap-2 border-t border-[var(--color-mockup-border)] bg-surface px-2 py-2">
        <input
          ref={inputRef}
          type="text"
          value={messageToSend}
          onChange={(e) => setMessageToSend(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          placeholder="Scrivi un messaggio…"
          className="h-9 flex-1 rounded-full border border-[var(--color-mockup-border)] bg-[var(--color-page)] px-3 text-sm text-[var(--color-mockup-text)] placeholder:text-[var(--color-mockup-text-light)] focus:border-primary focus:outline-none"
        />
        <button
          type="button"
          onClick={sendMessage}
          disabled={!messageToSend.trim()}
          aria-label="Invia messaggio"
          className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default FloatingChatWindow;
